import { find } from 'lodash';
import { injectable } from 'tsyringe';
import { BaseApi } from '../BaseApi';
import { LegalDocumentClassifications, PolicyOnAccount, PolicyValuesType } from '../constants/lookups';
import {
    AgreementContentType,
    DocumentItem,
    LegalDocumentContent,
    LegalDocumentWithExtraInfo,
} from '../types/Document.type';
import { formatDocumentData, formatLegalDocumentData } from '../utils';
import { IDocumentApi } from './interfaces/IDocumentApi';

@injectable()
export class DocumentApi extends BaseApi implements IDocumentApi {
    private REQUEST_CONFIG = {
        responseType: 'blob' as const,
    };

    async getAccountDocuments(
        accountNumber: string,
        docType: string,
        fromDate?: string,
        toDate?: string,
    ): Promise<DocumentItem[]> {
        const response = await this.sdks.orbis.get('documents/list', undefined, {
            params: {
                account: accountNumber,
                documentType: docType,
                fromMonth: fromDate,
                toMonth: toDate,
            },
        });

        return formatDocumentData(response.data.documentList);
    }

    async getPolicyValues(type: PolicyValuesType): Promise<string[]> {
        const response = await this.sdks.orbis.get(
            // @ts-ignore: URL not in SDK
            'user/legal/policies/listing',
            undefined,
            {
                urlQuery: type,
            },
        );
        return response.data;
    }
    async updatePolicyOnAccount(
        accountId: number,
        policy: PolicyOnAccount,
        type: PolicyValuesType,
    ): Promise<{ success: boolean }> {
        const response = await this.sdks.orbis.post(
            // @ts-ignore: URL not in SDK
            'user/legal/policy',
            {
                account: { accountId },
                policy,
                esigned: true,
            },
            {
                urlQuery: type,
            },
        );
        return response.data;
    }

    async getLegalDocuments(classification: LegalDocumentClassifications): Promise<LegalDocumentWithExtraInfo[]> {
        const response = await this.sdks.orbis.get(
            // @ts-ignore: URL not in SDK
            `user/legal/documents`,
            undefined,
            { params: { classification } },
        );

        if (response.data !== null && response.data.length > 0) {
            return formatLegalDocumentData(response.data);
        } else {
            return [];
        }
    }

    async getFundingAgreements(): Promise<AgreementContentType | null> {
        let response = await this.sdks.orbis.get(
            // @ts-ignore: URL not in SDK
            '/user/legal/documents',
            undefined,
            { params: { classification: LegalDocumentClassifications.Funding } },
        );

        const id = find(response.data, (list) => list.contentType === 'JSON').id;

        if (id) {
            response = await this.sdks.orbis.get(
                // @ts-ignore: URL not in SDK
                '/user/legal/document/content',
                undefined,
                { params: { id } },
            );
            return response.data;
        } else {
            return null;
        }
    }

    async downloadDocument(accountNumber: string, key: string): Promise<Blob> {
        const response = await this.sdks.orbis.get(
            // @ts-ignore: URL not in SDK
            'documents/download',
            undefined,
            {
                ...this.REQUEST_CONFIG,
                params: {
                    account: accountNumber,
                    key: key,
                },
            },
        );
        return response.data;
    }

    async downloadLegalDocument(id: string): Promise<Blob> {
        const response = await this.sdks.orbis.get(
            // @ts-ignore: URL not in SDK
            'user/legal/document/download',
            undefined,
            {
                ...this.REQUEST_CONFIG,
                params: {
                    id: id,
                },
            },
        );
        return response.data;
    }

    async getLegalDocumentContent(id: string | number): Promise<LegalDocumentContent> {
        const response = await this.sdks.orbis.get(
            // @ts-ignore: URL not in SDK
            'user/legal/document/content',
            undefined,
            {
                params: {
                    id: id,
                },
            },
        );

        return response.data;
    }

    async signLegalDocument(params: {
        esigned: boolean;
        documentId: string | number;
        signature: string;
    }): Promise<{ success: true }> {
        // @ts-ignore: URL not in SDK
        const response = await api.orbis.post('user/legal/document/sign', params);
        return response.data;
    }
}
